import MobxReactForm from 'mobx-react-form'
import Validatorjs from 'validatorjs'
import dvr from 'mobx-react-form/lib/validators/DVR'
import {
  convertRgbaToString,
  addThemeCssTag,
  filterUrlUnFriendlyChars,
} from '../../../helpers/generalHelpers'
import {
  addGeneralTheme,
  addContentTheme,
  addButtonTheme,
  addTextTheme,
  addBorderTheme,
  addBackgroundImage,
  addBackgroundShadow,
  addGeneralFont,
  addButtonTextTheme,
} from './formMakerHelpers'
import { getRawMessage } from '../../../locale'

import { isInternationalVersion } from '../../../settings/locale.settings'
import { customTextFields } from './customTextFields'

const systemFont = isInternationalVersion() ? 'ff-mark-pro' : 'dana'

//
// ─── FIELD IMPLEMENTED IN MVP ─────────────────────────────────────────────────────────
//

// 'fields'
// 'logo'
// 'title'  // required: true
// 'slug'
// 'description'
// 'success_message'
// 'max_submit_count'
// 'submit_start_time'
// 'submit_end_time'
// 'field_color'
// 'theme_name' : dark or light
// 'button_text'
// "active" : active or de-active
// 'send_emails'
// 'primary_email'
// 'text_color'
// 'button_color'
// 'submit_text_color'
// 'border_color'

//
// ─── FIELDS NOT IMPLEMENTED IN MVP ─────────────────────────────────────────────────────
//

// 'error_message'
// 'needs_login'
// 'logo_position'
// 'is_private'

// ────────────────────────────────────────────────────────────────────────────────

/**
 * FormzFormMaker is responsible for holding the all the fields of
 * the form under construction, except for the form fields.
 */

export class FormzFormMaker extends MobxReactForm {
  bindings() {
    return {
      formzField: ({ $try, field, props }) => ({
        // these are the default bindings
        id: $try(props.id, field.id),
        name: $try(props.name, field.name),
        type: $try(props.type, field.type),
        value: $try(props.value, field.value),
        label: $try(props.label, field.label),
        placeholder: $try(props.placeholder, field.placeholder),
        disabled: $try(props.disabled, field.disabled),
        onChange: $try(props.onChange, field.onChange),
        onBlur: $try(props.onBlur, field.onBlur),
        onFocus: $try(props.onFocus, field.onFocus),
        autoFocus: $try(props.autoFocus, field.autoFocus),

        // these two new fields are custom fields
        error: field.errors(),
        touched: field.touched,
      }),
    }
  }
}

const fields = [
  {
    name: 'slug',
    label: 'لینک دسترسی فرم',
    rules: 'required',
  },
  {
    name: 'title',
    bindings: 'formzField',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.formTitle.label'
    ),
    rules: 'required',
  },
  {
    name: 'address',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.tabs.mainSettings.form.fields.formAddress.label'
    ),
    rules: 'required',
  },
  {
    name: 'category',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.tabs.mainSettings.form.fields.formCategory.label'
    ),
    placeholder: getRawMessage(
      'pages.formMaker.section.formSettings.tabs.mainSettings.form.fields.formCategory.placeholder'
    ),
    output: (category) => {
      if (category && typeof category === 'string') return category
      return category && category.slug
    },
  },
  {
    name: 'description',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.description.label'
    ),
  },
  {
    // this is the color of the contents of the form
    name: 'field_color',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.themeColor.label'
    ),
    value: {
      r: 255,
      g: 255,
      b: 255,
      a: 1,
    },
  },
  {
    // this is the color of the contents of the form
    name: 'text_color',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.textColor.label'
    ),
    value: {
      r: 0,
      g: 0,
      b: 0,
      a: 1,
    },
  },
  {
    // this is the color of the contents of the form
    name: 'button_color',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.buttonColor.label'
    ),
    value: {
      r: 0,
      g: 0,
      b: 0,
      a: 1,
    },
  },
  {
    // this is the color of the submit button text of the form
    name: 'submit_text_color',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.submitTextColor.label'
    ),
    value: {
      r: 255,
      g: 255,
      b: 255,
      a: 1,
    },
  },
  {
    // this is the color of the submit button text of the form
    name: 'border_color',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.borderColor.label'
    ),
    //default value of borders are transparent
    value: {
      r: 216,
      g: 224,
      b: 237,
      a: 1,
    },
  },
  {
    // theme-name is the general theme color of the whole form
    // this should be changed in the future to something like: general_theme_color
    name: 'background_color',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.themeName.label'
    ),
    value: {
      r: 255,
      g: 255,
      b: 255,
      a: 1,
    },
  },
  {
    // only used in multi-step forms
    name: 'background_image',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.backgroundImage.label'
    ),
  },
  {
    name: 'logo',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.logo.label'
    ),
  },
  {
    // used for preview of templates and forms on dashboard
    name: 'thumbnail',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.thumbnail.label'
    ),
  },
  {
    name: 'fields',
    label: '',
    fields: [],
  },
  {
    name: 'time_limit',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.timeLimit.label'
    ),
    placeholder: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.timeLimit.format'
    ),
    value: 'null',
  },
  {
    name: 'success_message',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.successMessage.label'
    ),
    value: `<div style="text-align: center;">
      <div>
        <svg class='fade-in-animation' width="104" height="104" viewBox="0 0 104 104" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M41.9522 73.0805H34.0957C32.3413 73.0805 30.9195 71.6587 30.9195 69.9043V62.0447C30.9195 61.2023 30.5838 60.3948 29.9885 59.7995L24.431 54.242C23.1897 53.0007 23.1897 50.9898 24.431 49.7517L30.0455 44.1372C30.606 43.5767 30.9195 42.8198 30.9195 42.0282V34.0957C30.9195 32.3413 32.3413 30.9195 34.0957 30.9195H41.9553C42.7977 30.9195 43.6052 30.5838 44.2005 29.9885L49.758 24.431C50.9993 23.1897 53.0102 23.1897 54.2483 24.431L59.8058 29.9885C60.4012 30.5838 61.2087 30.9195 62.051 30.9195H69.9107C71.665 30.9195 73.0868 32.3413 73.0868 34.0957V41.9553C73.0868 42.7977 73.4225 43.6052 74.0178 44.2005L79.5753 49.758C80.8167 50.9993 80.8167 53.0102 79.5753 54.2483L74.0178 59.8058C73.4225 60.4012 73.0868 61.2087 73.0868 62.051V69.9107C73.0868 71.665 71.665 73.0868 69.9107 73.0868H62.051C61.2087 73.0868 60.4012 73.4225 59.8058 74.0178L54.2483 79.5753C53.007 80.8167 50.9962 80.8167 49.758 79.5753L44.2005 74.0178C43.602 73.413 42.7945 73.0805 41.9522 73.0805Z" fill="#F0F9F4" stroke="#34C759" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M61.5 47.25L49.625 59.125L42.5 52" stroke="#34C759" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M66.8293 88.2322L65.1123 83.9826" stroke="#DAF0E3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M38.8888 19.0778L37.1719 14.8282" stroke="#DAF0E3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M89.2092 67.3221L83.5139 64.9045" stroke="#DAF0E3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M14.7067 35.8595L20.4563 38.3" stroke="#DAF0E3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M67.7168 14.3494L65.2606 20.0603" stroke="#DAF0E3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M36.2524 88.8551L38.6543 83.1211" stroke="#DAF0E3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M19.3947 64.7029L15.2996 66.3574" stroke="#DAF0E3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M88.7013 36.701L84.6063 38.3556" stroke="#DAF0E3" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <p>${getRawMessage('pages.formMaker.success.message.default')}</p>
    </div>`,
  },
  {
    name: 'max_submit_count',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.maxSubmitCount.label'
    ),
    placeholder: '\u221E',
  },
  {
    name: 'submit_start_time',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.activeFrom.label'
    ),
    rules: 'date',
  },
  {
    name: 'submit_end_time',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.activeTo.label'
    ),
    rules: 'date',
  },
  {
    name: 'active',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.formActiveState.label'
    ),
    value: true,
  },
  {
    name: 'accept_draft_answers',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.partialSubmit.label'
    ),
    value: false,
  },
  {
    name: 'shuffle_fields',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.randomFields.label'
    ),
    value: false,
  },
  {
    name: 'show_title',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.formShowTitle.label'
    ),
    value: true,
  },
  {
    name: 'button_text',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.submitButtonText.label'
    ),
  },
  {
    name: 'send_emails_to',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.tabs.notificationSettings.fields.sendEmailTo.label'
    ),
    rules: 'email',
  },
  {
    name: 'send_user_confirm',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.tabs.notificationSettings.fields.sendEmailNotificationsToSubmitter.label'
    ),
    value: false,
  },
  {
    name: 'submit_email_notif',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.tabs.notificationSettings.fields.receiveEmailNotifications.label'
    ),
    value: false,
  },
  {
    name: 'form_redirects_after_submit',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.redirectFormAfterSubmit.label'
    ),
    value: null,
  },
  {
    name: 'include_data_on_redirect',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.includeDataOnRedirect.label'
    ),
    value: false,
  },
  {
    name: 'config',
    label: '',
    value: '',
  },
  {
    name: 'payment_method',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.paymentMethod'
    ),
    placeholder: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.paymentMethod'
    ),
    value: null,
    output: (payment) => {
      if (payment && typeof payment === 'string') return payment
      return payment && payment.name
    },
  },
  {
    name: 'fixed_payment_amount',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.fixedPaymentAmount'
    ),
    value: null,
  },
  {
    name: 'redirection_address',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.redirectionAddress'
    ),
    value: null,
  },
  {
    name: 'form_type',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.formType.label'
    ),
  },
  {
    name: 'show_submit_tracking_code',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.trackingCode.label'
    ),
    value: false,
  },
  {
    name: 'max_submit_per_ip_per_day',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.maxSubmitPerIP.label'
    ),
    value: 0,
  },
  {
    name: 'admin_email_notif_template',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.adminEmailTemplate.label'
    ),
    placeholder: getRawMessage('email.template.default'),
    value: null,
    output: (template) => {
      if (template && typeof template === 'string') return template
      return template && template.slug
    },
  },
  {
    name: 'user_email_notif_template',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.userEmailTemplate.label'
    ),
    placeholder: getRawMessage('email.template.default'),
    value: null,
    output: (template) => {
      if (template && typeof template === 'string') return template
      return template && template.slug
    },
  },
  {
    name: 'custom_css',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.customCss'
    ),
    value:
      '.formaloo__form-container {}\r\n.formaloo__form-title {}\r\n.formaloo__form-logo {}\r\n.formaloo__form-description {}\r\n.formaloo__field-container {}\r\n.formaloo__field-title {}\r\n.formaloo__field-description {}\r\n.formaloo__field-input {}\r\n.formaloo__field-section {} \r\n.formaloo__field-video {} \r\n.formaloo__button {}\r\n.formaloo__submit-button {}\r\n.formaloo__navigation-button {}\r\n.formaloo__timer {}\r\n.formaloo__timer-times-up {}',
  },
  {
    name: 'custom_js',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.customJS'
    ),
    value: '',
  },
  {
    name: 'currency',
    value: '',
  },
  {
    name: 'forward_submit_emails_to',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.emailForwards'
    ),
    placeholder: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.emailForwards.placeholder'
    ),
    value: [],
  },
  {
    // this is a json field for additional configs. if you want to add new key, set initial value for it to prevent removing others
    name: 'theme_config',
    label: getRawMessage(
      'pages.formMaker.section.formSettings.generalFields.formType.label'
    ),
    fields: [
      {
        name: 'background_type',
        value: 'full_page',
      },
      {
        name: 'background_shadow',
        value: {
          r: 0,
          g: 0,
          b: 0,
          a: 0,
        },
      },
      {
        name: 'progress_percentage',
        value: true,
      },
      {
        name: 'display_welcome_page',
        value: true,
      },
      {
        label: getRawMessage(
          'pages.formMaker.section.formSettings.generalFields.styleMode.modern.label'
        ),
        name: 'modern_view',
        value: false,
      },
      // NPS widget settings
      {
        name: 'widget_settings',
        fields: [
          {
            name: 'position',
            value: 'right',
          },
          {
            name: 'type',
            value: 'side-widget',
          },
          {
            name: 'once_per_user',
            value: false,
          },
        ],
      },
      {
        name: 'google_font',
        value: null,
        label: getRawMessage(
          'pages.formMaker.section.formSettings.generalFields.font.label'
        ),
      },
      {
        name: 'single_step_layout',
        value: 'two_columns', // two_columns or one_column
        label: getRawMessage(
          'pages.formMaker.section.formSettings.generalFields.layout'
        ),
      },
      {
        name: 'customized_texts',
        label: getRawMessage(
          'pages.formMaker.section.formSettings.generalFields.custom.text'
        ),
        fields: customTextFields,
      },
    ],
  },
]

// Add some additional data to all items in fields array
fields.forEach((field) => {
  field.bindings = 'formzField'
})

// set the plugins
const plugins = {
  dvr: dvr(Validatorjs),
}

// default hooks
const hooks = {
  onSuccess() {},
  onError() {},
}
const options = { validateOnChange: true }
// form factory
const form = new FormzFormMaker({ fields }, { plugins, options, hooks })

// helper functions
const readValueOf = (key) => form.$(key).value
export const getFormSlug = () => readValueOf('slug')
export const getFormCategory = () => readValueOf('category')
export const getFormAddress = () => readValueOf('address')
export const getFormThemeName = () => readValueOf('background_color')
export const getFormThemeColor = () => readValueOf('theme_color')
export const getFormFieldColor = () => readValueOf('field_color')
const exists = (value) => Boolean(value !== null && value !== undefined)

// initial values setter helpers
export const setInitialValues = (initialValues) => {
  if (!initialValues) return
  form.each((field) => {
    if (
      [
        'background_color',
        'field_color',
        'text_color',
        'button_color',
        'submit_text_color',
        'border_color',
      ].includes(field.name)
    ) {
      if (exists(initialValues[field.name])) {
        const rgbObject = JSON.parse(initialValues[field.name])
        field.onChange(rgbObject)
      }
    } else if (field.name === 'theme_config') {
      // theme_config is a nested field and should be updated by set() method
      if (
        exists(initialValues.theme_config) &&
        typeof initialValues.theme_config === 'object'
      ) {
        field.set(initialValues.theme_config)
      }
    } else {
      if (exists(initialValues[field.name])) {
        field.onChange(initialValues[field.name])
      }
    }
  })
}

// set interceptor for address to filter the none-url-friendly character
form.intercept({
  path: 'address',
  key: 'value',
  call: ({ change }) => {
    change.newValue = filterUrlUnFriendlyChars(change.newValue)
    return change
  },
})

// set observers to for theme-related fields.
form.$('background_color').observe(({ form, field, change }) => {
  const rgbaColor = convertRgbaToString(field.value)
  const styleSheet = addThemeCssTag()
  addGeneralTheme(styleSheet, rgbaColor)
})

form.$('background_image').observe(({ form, field, change }) => {
  const backgroundImage = field.value
  const styleSheet = addThemeCssTag()
  addBackgroundImage(styleSheet, backgroundImage)
})

form.$('form_type').observe(({ form, field, change }) => {
  const formType = field.value
  const backgroundImage = form.$('background_image').$value
  const styleSheet = addThemeCssTag()
  if (formType === 'multi_step') {
    addBackgroundImage(styleSheet, backgroundImage)
  }
  if (formType === 'nps') {
    addBackgroundImage(styleSheet, null)
  }
})

form.$('theme_config').observe(({ form, field, change }) => {
  const formType = form.$('form_type').$value

  const fontFamily =
    field.value && field.value.google_font /* Font+Name */
      ? field.value.google_font.split('+').join(' ') /* Font Name */
      : systemFont

  const isModernView = field?.value?.modern_view

  const backgroundShadow = convertRgbaToString(
    field.value && field.value.background_shadow
  )
  // themeConfigs.background_type === 'full_page' ||  !themeConfigs.background_type || (!isModernView && isMultiStep)
  const isFullPageBackground =
    !field?.value?.background_type ||
    field.value?.background_type === 'full_page' ||
    (!isModernView && formType === 'multi_step')

  const backgroundImage = !isFullPageBackground
    ? null
    : form.$('background_image').$value

  const styleSheet = addThemeCssTag()
  addBackgroundShadow(styleSheet, backgroundShadow)
  addBackgroundImage(styleSheet, backgroundImage)
  addGeneralFont(styleSheet, fontFamily)
})

form.$('field_color').observe(({ form, field, change }) => {
  const rgbaColor = convertRgbaToString(field.value)
  const styleSheet = addThemeCssTag()
  addContentTheme(styleSheet, rgbaColor)
})

form.$('border_color').observe(({ form, field, change }) => {
  const rgbaColor = convertRgbaToString(field.value)
  const styleSheet = addThemeCssTag()
  addBorderTheme(styleSheet, rgbaColor)
})

form.$('button_color').observe(({ form, field, change }) => {
  const rgbaColor = convertRgbaToString(field.value)
  const styleSheet = addThemeCssTag()
  addButtonTheme(styleSheet, rgbaColor)
})

form.$('submit_text_color').observe(({ form, field, change }) => {
  const rgbaColor = convertRgbaToString(field.value)
  const styleSheet = addThemeCssTag()
  addButtonTextTheme(styleSheet, rgbaColor)
})
form.$('text_color').observe(({ form, field, change }) => {
  const rgbaColor = convertRgbaToString(field.value)
  const styleSheet = addThemeCssTag()
  addTextTheme(styleSheet, rgbaColor)
})

export default form
